import React from 'react';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { TelemetryPoint } from '../context/TelemetryContext';
import { Sensor } from '../context/MachineContext';

interface Props {
  sensor:    Sensor;
  history:   TelemetryPoint[];
  status:    'ok' | 'warning' | 'critical';
  height?:   number;
  sparkline?: boolean; // compact mode — no axes, no grid
}

const STATUS_COLOR: Record<string, string> = {
  ok:       '#34d399',
  warning:  '#fbbf24',
  critical: '#f87171',
};

const TelemetryChart: React.FC<Props> = ({ sensor, history, status, height = 120, sparkline = false }) => {
  const color = STATUS_COLOR[status];
  const gradId = `grad_${sensor.sensorId}`;

  if (sparkline) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={history} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0}   />
            </linearGradient>
          </defs>
          <Area
            type="monotone" dataKey="value"
            stroke={color} fill={`url(#${gradId})`}
            strokeWidth={1.5} dot={false} isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={history} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0}    />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
        <XAxis
          dataKey="time"
          stroke="#4b5563"
          tick={{ fontSize: 9, fill: '#6b7280' }}
          interval="preserveStartEnd"
        />
        <YAxis
          stroke="#4b5563"
          tick={{ fontSize: 9, fill: '#6b7280' }}
          domain={[
            Math.min(sensor.minThreshold * 0.85, Math.min(...history.map(h => h.value)) * 0.95),
            Math.max(sensor.maxThreshold * 1.1,  Math.max(...history.map(h => h.value)) * 1.05),
          ]}
        />
        <Tooltip
          contentStyle={{ background: '#111827', border: '1px solid #1f2937', fontSize: 12 }}
          formatter={(v: any) => [`${Number(v).toFixed(2)} ${sensor.unit}`, sensor.name]}
          labelStyle={{ color: '#9ca3af' }}
        />
        <ReferenceLine y={sensor.maxThreshold} stroke="#f87171" strokeDasharray="4 2" strokeWidth={1} label={{ value: `Max ${sensor.maxThreshold}`, fill: '#f87171', fontSize: 9 }} />
        <ReferenceLine y={sensor.minThreshold} stroke="#60a5fa" strokeDasharray="4 2" strokeWidth={1} label={{ value: `Min ${sensor.minThreshold}`, fill: '#60a5fa', fontSize: 9 }} />
        <Area
          type="monotone" dataKey="value"
          stroke={color} fill={`url(#${gradId})`}
          strokeWidth={2} dot={false} isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default TelemetryChart;
